import { fetchJSON as fetchAPI } from './client';

export type DeveloperDashboard = {
  tenant_id: string;
  applications: number;
  active_api_keys: number;
  webhooks: number;
  extensions: number;
  marketplace_installs: number;
  api_calls_24h: number;
  error_rate_24h: number;
  avg_latency_ms: number;
  rate_limit_used_pct: number;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type Scope = {
  key: string;
  name: string;
  description?: string;
  category?: string;
};

export type PublicAPIEndpoint = {
  path: string;
  method: string;
  summary: string;
  category: string;
  version: string;
  scopes?: string[];
  deprecated?: boolean;
};

export type DeveloperApplication = {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  client_id: string;
  client_secret?: string;
  redirect_urls?: string[];
  scopes?: string[];
  auth_method: string;
  status: string;
  sandbox: boolean;
  api_calls_24h?: number;
  created_at: string;
  updated_at: string;
};

export type APIKeyRecord = {
  id: string;
  tenant_id: string;
  app_id?: string;
  name: string;
  key_prefix: string;
  scopes?: string[];
  revoked: boolean;
  created_at: string;
};

export type DeveloperWebhook = {
  id: string;
  tenant_id: string;
  app_id?: string;
  url: string;
  events?: string[];
  status: string;
  version?: string;
  delivery_count: number;
  failure_count: number;
  last_delivery_at?: string;
  created_at: string;
};

export type StreamEvent = {
  id: string;
  tenant_id: string;
  type: string;
  source: string;
  payload?: Record<string, unknown>;
  published_at: string;
};

export type ExtensionRecord = {
  id: string;
  tenant_id: string;
  app_id?: string;
  type: string;
  name: string;
  version: string;
  entry_point?: string;
  permissions?: string[];
  sandboxed: boolean;
  active: boolean;
  created_at: string;
};

export type MarketplaceApp = {
  id: string;
  name: string;
  description?: string;
  vendor?: string;
  version: string;
  category?: string;
  rating?: number;
  installs?: number;
  scopes?: string[];
  featured?: boolean;
  price_cents?: number;
};

export type AppInstallation = {
  id: string;
  tenant_id: string;
  workspace_id?: string;
  app_id: string;
  version: string;
  active: boolean;
  installed_at: string;
};

export type SDKPackage = {
  language: string;
  version: string;
  download_url?: string;
  changelog?: string;
  stable: boolean;
};

export type SandboxEnvironment = {
  tenant_id: string;
  active: boolean;
  mock_services?: string[];
  test_data_sets?: Record<string, number>;
  base_url?: string;
  generated_at: string;
};

export type APIAnalyticsSummary = {
  tenant_id: string;
  total_calls: number;
  error_count: number;
  error_rate: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
  by_endpoint?: Record<string, number>;
  by_application?: Record<string, number>;
  rate_limit_hits: number;
  webhook_deliveries: number;
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type DeveloperNotification = {
  id: string;
  title: string;
  body?: string;
  severity?: string;
  read: boolean;
  created_at: string;
};

export type OAuthTokenResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scopes?: string[];
};

const API_BASE = import.meta.env.VITE_DEVELOPER_API ?? '/api/v1/developer';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const developerApi = {
  dashboard: () => fetchJSON<DeveloperDashboard>('/dashboard'),

  listScopes: () => fetchJSON<Scope[]>('/scopes'),

  publicAPIs: () => fetchJSON<PublicAPIEndpoint[]>('/apis'),

  listApplications: () => fetchJSON<DeveloperApplication[]>('/applications'),

  registerApplication: (body: {
    name: string;
    description?: string;
    redirect_urls?: string[];
    scopes?: string[];
    auth_method?: string;
    sandbox?: boolean;
  }) => fetchJSON<DeveloperApplication>('/applications', { method: 'POST', body: JSON.stringify(body) }),

  suspendApplication: (id: string) =>
    fetchJSON<DeveloperApplication>(`/applications/${id}/suspend`, { method: 'POST' }),

  rotateSecret: (id: string) =>
    fetchJSON<DeveloperApplication>(`/applications/${id}/rotate-secret`, { method: 'POST' }),

  listAPIKeys: () => fetchJSON<APIKeyRecord[]>('/api-keys'),

  createAPIKey: (body: { name: string; app_id?: string; scopes?: string[] }) =>
    fetchJSON<{ key: APIKeyRecord; secret: string }>('/api-keys', { method: 'POST', body: JSON.stringify(body) }),

  listWebhooks: () => fetchJSON<DeveloperWebhook[]>('/webhooks'),

  registerWebhook: (body: { url: string; app_id?: string; events?: string[] }) =>
    fetchJSON<DeveloperWebhook>('/webhooks', { method: 'POST', body: JSON.stringify(body) }),

  replayWebhook: (id: string) =>
    fetchJSON<DeveloperWebhook>(`/webhooks/${id}/replay`, { method: 'POST' }),

  listEvents: (limit = 20) => fetchJSON<StreamEvent[]>(`/events?limit=${limit}`),

  listExtensions: () => fetchJSON<ExtensionRecord[]>('/extensions'),

  registerExtension: (body: { name: string; type: string; app_id?: string; entry_point?: string }) =>
    fetchJSON<ExtensionRecord>('/extensions', { method: 'POST', body: JSON.stringify(body) }),

  marketplace: (category?: string, search?: string) => {
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    const q = params.toString();
    return fetchJSON<MarketplaceApp[]>(`/marketplace${q ? `?${q}` : ''}`);
  },

  installApp: (body: { app_id: string; workspace_id?: string }) =>
    fetchJSON<AppInstallation>('/marketplace/install', { method: 'POST', body: JSON.stringify(body) }),

  listInstallations: () => fetchJSON<AppInstallation[]>('/marketplace/installations'),

  sdks: () => fetchJSON<SDKPackage[]>('/sdks'),

  sandbox: () => fetchJSON<SandboxEnvironment>('/sandbox'),

  oauthToken: (body: { client_id: string; client_secret?: string; grant_type: string }) =>
    fetchJSON<OAuthTokenResponse>('/oauth/token', { method: 'POST', body: JSON.stringify(body) }),

  analytics: () => fetchJSON<APIAnalyticsSummary>('/analytics'),

  listActivity: (limit = 15) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),

  listNotifications: (unread = true) => fetchJSON<DeveloperNotification[]>(`/notifications?unread=${unread}`),

  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
};

const GATEWAY_BASE = import.meta.env.VITE_GATEWAY_API ?? '/api/v1/gateway';

function fetchGateway<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(GATEWAY_BASE, path, init);
}

export type RegisteredAPI = {
  id: string;
  tenant_id?: string;
  name: string;
  service: string;
  owner: string;
  description?: string;
  openapi_version: string;
  lifecycle: string;
  authentication: string;
  authorization: string;
  rate_limit_rps: number;
  sla: string;
  version: string;
  consumers?: string[];
  tags?: string[];
  deprecation_status: string;
  protocol: string;
  base_path: string;
  port?: number;
  product_family?: string;
  registered_at: string;
};

export type OpenAPIRegistryEntry = {
  id: string;
  api_name: string;
  service: string;
  version: string;
  openapi_version: string;
  spec_url: string;
  backward_compatible: boolean;
  breaking_change_detected: boolean;
  compatibility_status: string;
  approval_status: string;
  security_model: string;
  published_at: string;
};

export type GatewaySDKEntry = {
  id: string;
  language: string;
  api_name: string;
  version: string;
  download_url: string;
  generated_at: string;
  stable: boolean;
};

export type GatewayDeveloperEntry = {
  id: string;
  name: string;
  email?: string;
  role: string;
  active: boolean;
  api_count: number;
  created_at: string;
};

export type APIPolicy = {
  id: string;
  name: string;
  policy_type: string;
  rate_limit_rps?: number;
  timeout_ms?: number;
  auth_policy?: string;
  retry_max?: number;
  applies_to?: string[];
  enabled: boolean;
};

export type GatewayHealth = {
  tenant_id: string;
  api_health_score: number;
  developer_experience_score: number;
  contract_compliance: number;
  registered_apis: number;
  published_apis: number;
  deprecated_apis: number;
  active_developers: number;
  openapi_specs: number;
  sdk_packages: number;
  avg_latency_ms: number;
  service_availability: number;
  validated_at: string;
};

export type GatewayAnalytics = {
  tenant_id: string;
  total_requests_24h: number;
  avg_latency_ms: number;
  error_rate: number;
  rate_limit_violations: number;
  sdk_downloads: number;
  developer_activity: number;
  gateway_throughput_rps: number;
  consumer_activity: number;
  validated_at: string;
};

export type OpenAPIValidationResult = {
  valid: boolean;
  compatibility_status: string;
  breaking_changes?: string[];
  consumer_impact?: string[];
  message: string;
};

export type GatewayDashboard = {
  tenant_id: string;
  apis: RegisteredAPI[];
  openapi: OpenAPIRegistryEntry[];
  sdks: GatewaySDKEntry[];
  developers: GatewayDeveloperEntry[];
  health: GatewayHealth;
  analytics: GatewayAnalytics;
  policies: APIPolicy[];
};

export const gatewayGovernanceApi = {
  listAPIs: () => fetchGateway<{ apis: RegisteredAPI[] }>('/apis').then((r) => r.apis),

  listOpenAPI: () => fetchGateway<{ openapi: OpenAPIRegistryEntry[] }>('/openapi').then((r) => r.openapi),

  listSDKs: () => fetchGateway<{ sdks: GatewaySDKEntry[] }>('/sdk').then((r) => r.sdks),

  listDevelopers: () => fetchGateway<{ developers: GatewayDeveloperEntry[] }>('/developers').then((r) => r.developers),

  publishAPI: (body: { api_id?: string; name?: string; version?: string }) =>
    fetchGateway<{ data: RegisteredAPI }>('/publish', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),

  deprecateAPI: (body: { api_id: string; reason?: string }) =>
    fetchGateway<RegisteredAPI>('/deprecate', { method: 'POST', body: JSON.stringify(body) }),

  analytics: () => fetchGateway<GatewayAnalytics>('/analytics'),

  health: () => fetchGateway<GatewayHealth>('/health'),

  policies: () => fetchGateway<{ policies: APIPolicy[] }>('/policies').then((r) => r.policies),

  validateOpenAPI: (body: { spec_id?: string; api_name?: string; version?: string }) =>
    fetchGateway<OpenAPIValidationResult>('/openapi/validate', { method: 'POST', body: JSON.stringify(body) }),

  dashboard: () => fetchGateway<GatewayDashboard>('/dashboard'),
};
